// Extra scroll width to compensate for the scroll buttons.
export const EXTRA_SCROLL_WIDTH = 16;

const getDynamicTableContainer = (): HTMLElement | null => {
	return document.querySelector('.js-dynamic-table-wrapper');
};

const getFixedTableContainer = (): HTMLElement | null => {
	return document.querySelector('.js-fixed-dynamic-table-wrapper');
};

const getDynamicTableElement = (tableContainer: HTMLElement): HTMLElement => {
	return tableContainer?.querySelector('table') as HTMLElement;
};

const getColumnElements = (table: HTMLElement) => table?.querySelectorAll('.js-table-header');

const getElementOffsetLeft = (el: HTMLElement) => el?.getBoundingClientRect().left;

const getElementOffsetRight = (el: HTMLElement) => el?.getBoundingClientRect().right;

/**
 * Calculates the position to which the table should be scrolled to.
 */
const getScrollPosition = (table: HTMLElement, offsetDiff: number) => table.scrollLeft + offsetDiff;

const scrollElementTo = (el: HTMLElement, posX: number, instant = false) => el.scrollTo({
	left: posX,
	behavior: `${instant ? 'instant' : 'smooth'}`
});

type TableElements = {
	tableContainer: HTMLElement;
	fixedTableContainer: HTMLElement;
	columns: NodeListOf<Element>;
}

/**
 * Retrieves the elements required to scroll the table.
 * Returns null if any of the elements is missing.
 */
const getElements = (): TableElements | null => {
	const fixedTableContainer = getFixedTableContainer();
	const tableContainer = getDynamicTableContainer();
	if (!fixedTableContainer || !tableContainer) {
		return null;
	}

	const tableElement = getDynamicTableElement(tableContainer);
	if (!tableElement) {
		return null;
	}

	const columns = getColumnElements(tableElement);
	if (!columns) {
		return null;
	}

	return {tableContainer, fixedTableContainer, columns};
};

/**
 * Scrolls the table to the previous column that is not fully visible.
 */
export const scrollToPrevColumn = () => {
	const elements = getElements();
	if (!elements) {
		return;
	}

	const {tableContainer, fixedTableContainer, columns} = elements;
	const containerOffsetLeft = getElementOffsetLeft(tableContainer) + EXTRA_SCROLL_WIDTH;
	const reversedColumns = Array.from(columns).reverse();

	for (const column of reversedColumns) {
		const columnOffsetLeft = getElementOffsetLeft(column as HTMLElement);
		const offsetDiff = Math.ceil(columnOffsetLeft) - Math.ceil(containerOffsetLeft);

		if (offsetDiff < 0) {
			const posX = getScrollPosition(tableContainer, offsetDiff);
			scrollElementTo(tableContainer, posX);
			scrollElementTo(fixedTableContainer, posX);
			break;
		}
	}
};

/**
 * Scrolls the table to the next column that is not fully visible.
 */
export const scrollToNextColumn = () => {
	const elements = getElements();
	if (!elements) {
		return;
	}

	const {tableContainer, fixedTableContainer, columns} = elements;
	const containerOffsetRight = getElementOffsetRight(tableContainer) - EXTRA_SCROLL_WIDTH;

	for (let i = 0; i < columns.length; i++) {
		const column = columns[i];
		const columnOffsetRight = getElementOffsetRight(column as HTMLElement);
		const offsetDiff = Math.floor(columnOffsetRight) - Math.floor(containerOffsetRight);

		if (offsetDiff > 0) {
			const posX = getScrollPosition(tableContainer, offsetDiff);
			scrollElementTo(tableContainer, posX);
			scrollElementTo(fixedTableContainer, posX);
			return;
		}
	}
};

/**
 * Scrolls the table to the given week.
 *
 * @param week The week to scroll to. Format: 'YYYYWW'.
 * @param instant Whether to scroll instantly.
 */
export const scrollWeekIntoView = (week: string, instant = false) => {
	if (!week) {
		return;
	}

	const elements = getElements();
	if (!elements) {
		return;
	}

	const {tableContainer, fixedTableContainer, columns} = elements;
	const containerOffsetLeft = getElementOffsetLeft(tableContainer) + EXTRA_SCROLL_WIDTH;

	for (let i = 0; i < columns.length; i++) {
		const col = columns[i] as HTMLElement;

		if (col.dataset.column === week) {
			const columnOffsetLeft = getElementOffsetLeft(col);
			const offsetDiff = Math.ceil(columnOffsetLeft) - Math.ceil(containerOffsetLeft);

			const posX = getScrollPosition(tableContainer, offsetDiff);
			scrollElementTo(tableContainer, posX, instant);
			scrollElementTo(fixedTableContainer, posX, instant);
			break;
		}
	}
};

/**
 * Synchronizes the scroll position of the dynamic table header with the fixed table header.
 */
export const syncDynamicTableHeaderScrollPosition = () => {
	const fixedTableContainer = getFixedTableContainer();
	const tableContainer = getDynamicTableContainer();

	if (!fixedTableContainer || !tableContainer) {
		return;
	}

	scrollElementTo(tableContainer, fixedTableContainer.scrollLeft, true);
};

/**
 * Synchronizes the scroll position of the fixed table header with the dynamic table header.
 */
export const syncFixedTableHeaderScrollPosition = () => {
	const fixedTableContainer = getFixedTableContainer();
	const tableContainer = getDynamicTableContainer();

	if (!fixedTableContainer || !tableContainer) {
		return;
	}

	scrollElementTo(fixedTableContainer, tableContainer.scrollLeft, true);
};
